import { platformBrowser } from '@angular/platform-browser';
import { boot } from './boot';
import { isRestrictedToCreditCardUpdateView, isEmbeddedWebView, isFleetDeviceView } from '@lib/boot-utils';
import { LoadTimeLogger, MEASURES_NAMES, MARKERS, LOG_TYPES } from '@lib/performance-logging';
import { mapLocale } from '@lib/map-locale';
import { isDriverSafetyView } from './mobile/driver-safety/util';
import { IResponseUser } from '@services/company.interface';
import { isBrowserNotSupported } from '@lib/check-browser-version';

const bootPerformanceLogger: LoadTimeLogger = new LoadTimeLogger(LOG_TYPES.BOOT);

// any changes done here should be reflected in index.ejs
function checkUserLocale(user: IResponseUser): boolean {
  if (isEmbeddedWebView()) return; // don't check user locale from account settings because it's provided by mobile

  const pathSegment: string[] = location.pathname.toLowerCase().split('/').filter(i => i);
  const currentLocale: string = pathSegment[pathSegment.length - 1];
  const userLocale: string = mapLocale(user.language);
  if (currentLocale !== userLocale.toLowerCase()) {
    let path = `/${userLocale}/`;
    if (pathSegment.length > 1) path = `/${pathSegment[0]}${path}`;
    location.href = location.origin + path + location.hash + location.search;
    return true;
  }
  return false;
}

function loadApp(user): void {
  if (user) {
    isBrowserNotSupported();
    // any changes done here should be reflected in index.ejs
    const isAuthRedirect: boolean = window.location.href.includes('?redirect=true');
    if (!isAuthRedirect && checkUserLocale(user)) return;
    const isCreditCardUpdateView: boolean = window.location.href.includes('update-credit-card');
    const isSmartLoadBoardView: boolean = window.location.href.includes('load-board');
    const isTeamDriving: boolean = window.location.href.includes('team-driving');
    const isPublicCardApplicationView: boolean = window.location.href.includes('apply-cards');

    if (isCreditCardUpdateView || isRestrictedToCreditCardUpdateView(user)) {
      loadModule(
        'CreditCardUpdateModule',
        import(/* webpackChunkName: "credit-card-update.module" */ './credit-card-update/module'),
      );
    } else if (isSmartLoadBoardView) {
      loadModule(
        'LoadBoardModule',
        import(/* webpackChunkName: "loadboard.module" */ './load-board/module'),
      );
    } else if (isDriverSafetyView) {
      loadModule(
        'DriverSafetyModule',
        import(/* webpackChunkName: "driver-safety.module" */ './mobile/driver-safety/module'),
      );
    } else if (isTeamDriving) {
      loadModule(
        'TeamDrivingModule',
        import(/* webpackChunkName: "team-driving.module" */ './mobile/team-driving/module'),
      );
    } else if (isFleetDeviceView) {
      loadModule(
        'DeviceModule',
        import(/* webpackChunkName: "device.module" */ './mobile/devices/module'),
      );
    } else if (isAuthRedirect) {
      loadModule(
        'AuthRedirectModule',
        import(/* webpackChunkName: "auth-redirect.module" */ './auth-redirect/module'),
      );
    } else if (isPublicCardApplicationView) {
      loadModule(
        'PublicCardsApplicationModule',
        import(/* webpackChunkName: "public-cards-application.module" */ './public-cards-application/module'),
      );
    } else {
      loadModule(
        'AppModule',
        import(/* webpackChunkName: "app-fleet-view.module" */ './app.module'),
      );
    }
  } else {
    loadModule(
      'PublicModule',
      import(/* webpackChunkName: "public.module" */ './public/module'),
    );
  }
}

async function loadModule(moduleKey: string, modulePromise: Promise<any>): Promise<any> {
  // BOOT ENDING MARKER
  window.performance.mark(MARKERS.BOOT_END);
  bootPerformanceLogger.measureFromMarker(MARKERS.BOOT_START, MARKERS.BOOT_END, MEASURES_NAMES.BOOT, { clearMarkers: false });
  // Clearing only the ending marker; as BOOT_START marker is used in DashboardModule.
  window.performance.clearMarks(MARKERS.BOOT_END);


  // ROOT MODULE STARTING MARKERS
  window.performance.mark(MARKERS.ROOT_MODULE_START);
  const namedRootModuleStartingKey: string = `${moduleKey}-${MARKERS.ROOT_MODULE_START}`;
  window.performance.mark(namedRootModuleStartingKey);

  const module = await modulePromise;

  return platformBrowser()
    .bootstrapModule(module[moduleKey])
    .then(() => {
      // ROOT MOUDLE ENDING MARKERS
      window.performance.mark(MARKERS.ROOT_MODULE_END);
      bootPerformanceLogger.measureFromMarker(MARKERS.ROOT_MODULE_START, MARKERS.ROOT_MODULE_END, MEASURES_NAMES.ROOT_MODULE);

      const namedRootModuleEndingKey: string = `${moduleKey}-${MARKERS.ROOT_MODULE_END}`;
      window.performance.mark(namedRootModuleEndingKey);
      bootPerformanceLogger.measureFromMarker(
        namedRootModuleStartingKey,
        namedRootModuleEndingKey,
        `${moduleKey}-${MEASURES_NAMES.ROOT_MODULE}`,
      );
      bootPerformanceLogger.flushPageLoadTimeLogs([`${moduleKey}-${MEASURES_NAMES.ROOT_MODULE}`, MEASURES_NAMES.ROOT_MODULE]);
    })
  ;
}

boot(loadApp);
